import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import Collapsible from 'modules/Collapsible/Collapsible';

import WorkspaceRowCheck from '../WorkspaceRowCheck';
import WorkspaceTitleButton from '../WorkspaceTitleButton';

interface WorkspaceItemRowProps {
  workspaceId: string;
  name: string;
  isSelected: boolean;
  hasChildren: boolean;
  depth: number;
  onSelect?: () => void;
  hasMemberCount?: boolean;
  memberCount?: number;
  className?: string;
  avatarUrl?: string;
  isChevronActive?: boolean;
}

const WorkspaceItemRow = ({
  workspaceId,
  name,
  isSelected,
  hasChildren,
  depth,
  onSelect,
  hasMemberCount,
  memberCount,
  className,
  avatarUrl,
  isChevronActive = false,
}: WorkspaceItemRowProps) => {
  const { t } = useTranslation();
  const isSubWorkspace = depth > 0;

  return (
    <div className={cx('relative flex items-center gap-0.5', { 'pl-5': !hasChildren }, className)}>
      {hasChildren && (
        <Collapsible.Head className="shrink-0 group/chevron !p-0">
          <Collapsible.Toggle
            hasText={false}
            variant="workspace"
            isActive={isChevronActive}
            arrowIconClassName={cx('w-[18px] h-[18px] transition-colors', {
              'stroke-gray-80': isChevronActive,
              'stroke-gray-50 group-hover/chevron:stroke-gray-80': !isChevronActive,
            })}
          />
        </Collapsible.Head>
      )}
      <div
        data-workspace-row-id={workspaceId}
        {...(isSelected ? { 'data-workspace-selected': true } : {})}
        role={onSelect ? 'button' : undefined}
        tabIndex={onSelect ? 0 : undefined}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (!onSelect || (event.key !== 'Enter' && event.key !== ' ')) return;

          event.preventDefault();
          onSelect();
        }}
        className={cx(
          'relative flex min-w-0 flex-1 items-center gap-2 rounded-lg py-1 pl-2 pr-3 hover:bg-gray-10',
          isSubWorkspace ? 'h-9' : 'h-10',
          { 'bg-primary-10': isSelected, 'cursor-pointer': onSelect }
        )}
      >
        <WorkspaceTitleButton
          name={name}
          depth={depth}
          avatarUrl={avatarUrl}
          compact={isSubWorkspace}
          className="min-w-0 flex-1 !p-0"
        />
        {hasMemberCount && (
          <span className="mr-2 max-w-fit flex-1 truncate text-xs text-gray-80">
            ({t('workspaces.members_count', { count: memberCount })})
          </span>
        )}
        <WorkspaceRowCheck isSelected={isSelected} />
      </div>
    </div>
  );
};

export default WorkspaceItemRow;
